import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { devHubGhecAuthApiRef } from '../../../apis';
import { usePersistedState } from '../../util/persistedState';
import { Message, Session } from './types';
import { THINKING_WORDS } from './constants';

/**
 * Custom hook for managing the Nova AI chat feature.
 */
export const useAgentChat = () => {
  const location = useLocation();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const githubAuthApi = useApi(devHubGhecAuthApiRef);

  const [sessions, setSessions] = usePersistedState<Session[]>(
    'agent-chat-sessions',
    { hours: 168 },
    [],
  );
  const [activeSessionId, setActiveSessionId] = usePersistedState<
    string | null
  >('agent-chat-active-session', { hours: 168 }, null);
  const [stoppedMessages, setStoppedMessages] = usePersistedState<
    Record<string, boolean>
  >('agent-chat-stopped-messages', { hours: 168 }, {});

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [thinkingWord, setThinkingWord] = useState('Thinking');
  const [error, setError] = useState<string | null>(null);
  const [githubConnected, setGithubConnected] = useState<boolean | null>(null);
  const [copied, setCopied] = useState<Record<string, boolean>>({});

  const bottomRef = useRef<HTMLDivElement>(null);
  const streamingRef = useRef('');
  const toolsCalledRef = useRef<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const rafRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentMessages: Message[] = useMemo(
    () => sessions.find(s => s.id === activeSessionId)?.messages ?? [],
    [sessions, activeSessionId],
  );

  useEffect(() => {
    githubAuthApi
      .getAccessToken(undefined, { optional: true })
      .then(t => setGithubConnected(!!t))
      .catch(() => setGithubConnected(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connectGitHub = async () => {
    try {
      await githubAuthApi.getAccessToken();
      setGithubConnected(true);
    } catch {
      setGithubConnected(false);
    }
  };

  useEffect(() => {
    if (sessions.length === 0) {
      const id = crypto.randomUUID();
      setSessions([{ id, title: '', createdAt: Date.now(), messages: [] }]);
      setActiveSessionId(id);
    } else if (
      !activeSessionId ||
      !sessions.find(s => s.id === activeSessionId)
    ) {
      setActiveSessionId(sessions[0].id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load Slack handoff context from ?ctx= query param
  useEffect(() => {
    const ctxKey = new URLSearchParams(location.search).get('ctx');
    if (!ctxKey) return undefined;

    const backendUrl = config.getString('backend.baseUrl');
    identityApi
      .getCredentials()
      .then(({ token }) =>
        fetch(`${backendUrl}/api/agent/context/${ctxKey}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      )
      .then(r => r.json())
      .then(({ history }) => {
        if (!history?.length) return;
        const id = crypto.randomUUID();
        setSessions(prev => [
          { id, title: 'From Slack', createdAt: Date.now(), messages: history },
          ...prev,
        ]);
        setActiveSessionId(id);
        window.history.replaceState({}, '', location.pathname);
      })
      .catch(() => {});

    return undefined;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading || streamingContent || toolStatus) return undefined;
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % THINKING_WORDS.length;
      setThinkingWord(THINKING_WORDS[i]);
    }, 800);
    return () => clearInterval(interval);
  }, [loading, streamingContent, toolStatus]);

  useEffect(() => {
    if (currentMessages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentMessages]);

  useEffect(() => {
    if (streamingContent) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingContent]);

  const createSession = () => {
    const id = crypto.randomUUID();
    setSessions(prev => [
      { id, title: '', createdAt: Date.now(), messages: [] },
      ...prev,
    ]);
    setActiveSessionId(id);
    setError(null);
    return id;
  };

  const switchSession = (id: string) => {
    if (id === activeSessionId) return;
    abortControllerRef.current?.abort();
    setActiveSessionId(id);
    setError(null);
  };

  const deleteSession = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessions(prev => {
      const remaining = prev.filter(s => s.id !== id);
      if (remaining.length === 0) {
        const newId = crypto.randomUUID();
        setActiveSessionId(newId);
        return [{ id: newId, title: '', createdAt: Date.now(), messages: [] }];
      }
      if (id === activeSessionId) setActiveSessionId(remaining[0].id);
      return remaining;
    });
  };

  const updateSessionMessages = (sessionId: string, msgs: Message[]) => {
    setSessions(prev =>
      prev.map(s => (s.id === sessionId ? { ...s, messages: msgs } : s)),
    );
  };

  const setSessionTitle = (sessionId: string, title: string) => {
    setSessions(prev =>
      prev.map(s => (s.id === sessionId && !s.title ? { ...s, title } : s)),
    );
  };

  const sendMessage = async () => {
    const message = input.trim();
    if (!message || loading) return;

    const sessionId = activeSessionId ?? createSession();
    const previousMessages = currentMessages;
    const newMessages: Message[] = [
      ...currentMessages,
      { role: 'user', content: message },
    ];

    updateSessionMessages(sessionId, newMessages);
    setInput('');
    setLoading(true);
    setError(null);
    setToolStatus(null);
    setStreamingContent('');
    streamingRef.current = '';
    toolsCalledRef.current = [];

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const makeAssistantMessage = (): Message => ({
      role: 'assistant',
      content: streamingRef.current,
      tools:
        toolsCalledRef.current.length > 0
          ? [...toolsCalledRef.current]
          : undefined,
    });

    try {
      const backendUrl = config.getString('backend.baseUrl');
      const [{ token }, githubToken] = await Promise.all([
        identityApi.getCredentials(),
        githubAuthApi
          .getAccessToken(undefined, { optional: true })
          .catch(() => undefined),
      ]);

      const response = await fetch(`${backendUrl}/api/agent/chat`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(githubToken ? { 'X-GitHub-Token': githubToken } : {}),
        },
        body: JSON.stringify({
          message,
          history: previousMessages.map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedDone = false;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          const eventMatch = part.match(/^event: (\w+)\ndata: (.+)$/s);
          if (!eventMatch) continue;
          const [, eventName, rawData] = eventMatch;
          const data = JSON.parse(rawData);

          if (eventName === 'token') {
            streamingRef.current += data.text;
            if (!rafRef.current) {
              rafRef.current = setTimeout(() => {
                setStreamingContent(streamingRef.current);
                rafRef.current = null;
              }, 80);
            }
          } else if (eventName === 'tool_call') {
            if (!toolsCalledRef.current.includes(data.name)) {
              toolsCalledRef.current = [...toolsCalledRef.current, data.name];
            }
            setToolStatus(`Using ${data.name}...`);
          } else if (eventName === 'tool_result') {
            setToolStatus(null);
          } else if (eventName === 'done') {
            receivedDone = true;
            setToolStatus(null);
            const finalMessages = [...newMessages, makeAssistantMessage()];
            updateSessionMessages(sessionId, finalMessages);
            setStoppedMessages(prev => {
              const next = { ...prev };
              delete next[`${sessionId}:${finalMessages.length - 1}`];
              return next;
            });
            if (!sessions.find(s => s.id === sessionId)?.title) {
              setSessionTitle(sessionId, message.slice(0, 40));
            }
          } else if (eventName === 'error') {
            throw new Error(data.message);
          }
        }
      }

      if (!receivedDone && streamingRef.current) {
        updateSessionMessages(sessionId, [
          ...newMessages,
          makeAssistantMessage(),
        ]);
        setSessionTitle(sessionId, message.slice(0, 40));
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        if (streamingRef.current) {
          const finalMessages = [...newMessages, makeAssistantMessage()];
          updateSessionMessages(sessionId, finalMessages);
          setSessionTitle(sessionId, message.slice(0, 40));
          setStoppedMessages(prev => ({
            ...prev,
            [`${sessionId}:${finalMessages.length - 1}`]: true,
          }));
        } else {
          updateSessionMessages(sessionId, previousMessages);
        }
      } else {
        setError(err.message ?? 'Something went wrong');
      }
    } finally {
      if (rafRef.current) {
        clearTimeout(rafRef.current);
        rafRef.current = null;
      }
      setLoading(false);
      setToolStatus(null);
      setStreamingContent('');
      streamingRef.current = '';
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCopy = (key: string, content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    });
  };

  const handleFeedback = (
    sessionId: string,
    msgIndex: number,
    value: 'up' | 'down',
  ) => {
    setSessions(prev =>
      prev.map(s => {
        if (s.id !== sessionId) return s;
        const messages = s.messages.map((m, i) =>
          i !== msgIndex
            ? m
            : { ...m, feedback: m.feedback === value ? null : value },
        );
        return { ...s, messages };
      }),
    );
  };

  return {
    sessions,
    activeSessionId,
    stoppedMessages,
    currentMessages,
    input,
    setInput,
    loading,
    streamingContent,
    toolStatus,
    thinkingWord,
    error,
    githubConnected,
    copied,
    bottomRef,
    abortControllerRef,
    createSession,
    switchSession,
    deleteSession,
    sendMessage,
    handleKeyDown,
    handleCopy,
    handleFeedback,
    connectGitHub,
  };
};
